import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useBlindReviewCreateModifyLog,
  useBlindReviewLogs,
  useBlindReviewRefreshReason,
} from '@/api-hooks/useBlindReview';

const Lottie = dynamic(() => import('react-lottie'), { ssr: false });
import Button from '@/components/v4/Button';
import { ModalXIconButton } from '@/components/v4/Modal/ModalPaper';
import { Review } from '@/types/review';

import Email from '../../assets/email.json';
import { ReasonErrorCard } from '../../components/BlindReview';
import { ArrowDownIcon, CopyableInfoCard, ReasonEditableCard, ReasonSkeletonCard } from '../shared';
import dayjs from 'dayjs';

export const BaeminCopyInfoContent = ({
  isOpen,
  review,
  onClose,
  title = '블라인드 신청 완료',
  showRequestedAt = false,
}: {
  isOpen: boolean;
  review: Review;
  onClose: () => void;
  title?: string;
  showRequestedAt?: boolean;
}) => {
  const [isWaitingForReason, setIsWaitingForReason] = useState(false);

  const { data: blindReviewLogsResponse, isLoading } = useBlindReviewLogs(
    { review_id: review.review_id },
    { enabled: isOpen, refetchInterval: isWaitingForReason ? 3000 : false },
  );
  const { mutate: refreshReason } = useBlindReviewRefreshReason();
  const { mutate: createModifyLog } = useBlindReviewCreateModifyLog();

  const blindLog = blindReviewLogsResponse?.data?.[0];

  useEffect(() => {
    if (isOpen && blindLog?.status === 'REQUESTED' && !blindLog?.reason)
      setIsWaitingForReason(true);
  }, [isOpen, blindLog?.status, blindLog?.reason]);

  useEffect(() => {
    if (blindLog?.reason && isWaitingForReason) setIsWaitingForReason(false);
  }, [blindLog?.reason, isWaitingForReason]);

  useEffect(() => {
    if (!isOpen) setIsWaitingForReason(false);
  }, [isOpen]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const reasonTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);

  const reviewData = blindLog?.review;
  const formattedReviewDate = reviewData?.created_at
    ? dayjs(reviewData.created_at).format('YYYY년 MM월 DD일')
    : review.created_at
      ? dayjs(review.created_at).format('YYYY년 MM월 DD일')
      : '-';
  const businessNumber = blindLog?.user?.business_number || '-';
  const isReasonError = blindLog?.reason === 'ERROR';
  const isReasonLoading = isWaitingForReason && !blindLog?.reason;
  const formattedRequestedAt = blindLog?.created_at
    ? formatRequestedAt(blindLog.created_at)
    : 'YY.MM.DD 00:00';

  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    setHasScrollableContent(scrollHeight > clientHeight);
    setIsScrolledToBottom(
      scrollHeight <= clientHeight || scrollHeight - scrollTop - clientHeight < 10,
    );
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsScrolledToBottom(false);
      setTimeout(checkScrollPosition, 100);
    }
  }, [isOpen, checkScrollPosition]);

  return (
    <div className="flex flex-col relative max-h-[90vh] md:h-[700px] md:max-h-none">
      <div className="flex items-center justify-between px-5 py-2.5 mt-2.5 shrink-0">
        <div className="flex items-center gap-2">
          <p className="text-black font-semibold text-lg">{title}</p>
          {showRequestedAt && (
            <span className="text-cds-coolgray-50 text-xs font-normal tracking-[-0.132px]">
              {formattedRequestedAt} 신청됨
            </span>
          )}
        </div>
        <ModalXIconButton onClick={onClose} dataGtmId="blind-complete-modal-close" />
      </div>

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-5 md:px-20 pb-40 pt-1 md:pt-2.5"
        onScroll={checkScrollPosition}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-cds-orange-55 border-t-transparent" />
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center pb-8">
              <Lottie
                options={{ loop: true, autoplay: true, animationData: Email }}
                width={90}
                height={90}
              />
              <p className="text-cds-black-99 font-semibold text-[18px] leading-[140%] mt-4 md:mt-2 text-center">
                배달앱 승인 후,
              </p>
              <p className="text-cds-black-99 font-semibold text-[18px] leading-[140%] text-center">
                신청서가 <span className="text-cds-orange-55">24시간 내 문자</span>로 전달돼요
              </p>
              <p className="text-cds-coolgray-65 text-[14px] font-normal leading-normal tracking-[-0.154px] mt-2">
                필요한 내용은 복사하여 제출해주세요
              </p>
            </div>

            <div className="flex flex-col gap-2.5">
              <CopyableInfoCard
                label="사업자번호"
                value={businessNumber}
                gtmId="blind-requested-copy-businessnumber"
              />
              <CopyableInfoCard
                label="리뷰 등록일자"
                value={formattedReviewDate}
                gtmId="blind-requested-copy-date"
              />
              <CopyableInfoCard
                label="리뷰 작성자"
                value={reviewData?.reviewer || review.reviewer}
                gtmId="blind-requested-copy-reviewer"
              />
              <CopyableInfoCard
                label="리뷰 내용"
                value={reviewData?.content || review.content || ''}
                multiline
                gtmId="blind-requested-copy-review"
                emptyText="내용이 없는 리뷰에요"
              />
              {isReasonLoading ? (
                <ReasonSkeletonCard />
              ) : isReasonError ? (
                <ReasonErrorCard
                  onRetry={() => {
                    refreshReason({ reviewId: review.review_id });
                    setIsWaitingForReason(true);
                  }}
                />
              ) : (
                <ReasonEditableCard
                  value={blindLog?.reason || ''}
                  textareaRef={reasonTextareaRef}
                  initialHighlight
                  logId={blindLog?.log_id}
                  onCopyReason={createModifyLog}
                  gtmId="blind-requested-copy-reason"
                />
              )}
            </div>
            <div ref={bottomRef} />
          </>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 mx-5 md:mx-5">
        <div
          className="px-5 md:px-20"
          style={{
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 90%)',
          }}
        >
          {hasScrollableContent && !isScrolledToBottom ? (
            <Button
              variant="contained"
              color="orange"
              className="w-full rounded-xl flex items-center justify-center gap-2"
              height={40}
              onClick={() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' })}
              data-gtm-id="blind-requested-end"
            >
              끝까지 확인하기 <ArrowDownIcon />
            </Button>
          ) : (
            <Button
              variant="tonal"
              color="coolgray"
              className="w-full rounded-xl"
              height={40}
              onClick={onClose}
            >
              닫기
            </Button>
          )}
        </div>
        <div className="h-6 bg-white" />
      </div>
    </div>
  );
};

const formatRequestedAt = (dateString: string): string => {
  const date = new Date(dateString);
  const year = String(date.getFullYear()).slice(-2);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}.${month}.${day} ${hours}:${minutes}`;
};
